import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropCell } from 'components/SpaceBooking/SpaceTable/DropCell';
import { RefetchContextProvider } from 'components/SpaceBooking/SpaceTable/RefetchContext';
import { SpaceTableLoader } from 'components/SpaceBooking/SpaceTable/SpaceTableLoader';
import { MappedSpace } from 'components/SpaceBooking/SpaceTable/types';
import { Button } from 'components/styledComponents/Button';
import Flex from 'components/styledComponents/Flex';
import { refTable } from 'components/TrainingPlan/TrainingPlanHelpers';
import { InjectProps } from 'hoc/InjectProps';
import { useWindowDimensions } from 'hooks';
import { maxBy, merge, minBy, range } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';
import BaseTable, { AutoResizer, ColumnShape } from 'react-base-table';
import 'react-base-table/styles.css';
import styled from 'styled-components';
import useSWR from 'swr';
import { SpaceGroup } from '../types';

const StyledVerticalSpaceTable = styled.div<{ size?: number }>`
    height: 100%;
    width: 100%;
    max-width: ${({ size }) => (size !== null ? size + 'px' : 'unset')};

    .BaseTable__table-frozen-left {
        box-shadow: 4px 0 5px 0 #eeeeee;
    }

    .BaseTable__table-frozen-left .BaseTable__row-cell:first-child {
        padding: 0;
        justify-content: center;
    }

    .BaseTable__table-main .BaseTable__row-cell {
        padding: 0;
    }

    .BaseTable__table-main .BaseTable__row-cell:last-child {
        padding-right: 0;
    }

    .BaseTable__row--hovered,
    .space-table-row:hover {
        background-color: inherit;
    }

    .BaseTable__row > .BaseTable__row-cell:first-of-type,
    .BaseTable__header-row > .BaseTable__header-cell:first-of-type {
        border-right: 1px solid rgb(200, 200, 200);
    }

    .space-table-cell {
        padding: 0;
        overflow: visible !important;
    }

    .space-table-header-row {
        height: 30px;
    }
`;

const SpaceTableFooter = styled.div`
    height: 55px;
    width: 100%;
    display: flex;
    border: 1px solid #eeeeee;

    > button.btn {
        border-radius: 0;
        height: 100%;

        flex: 1 1 auto;

        svg {
            margin: 0 1rem;
        }
    }
`;

const HeaderGroupCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 100%;

    &:not(:last-child) {
        border-right: 2px solid lightgray;
    }
`;

interface Props {
    spaceGroup: SpaceGroup;
    viewSettings?: any;
    className?: string;
    isOwner?: boolean;
}

export const VerticalSpaceTable = ({ spaceGroup, className, isOwner = false }: Props) => {
    const columnWidth = 160;
    const tableRef = useRef<BaseTable>(undefined);
    const [weekOffset, setWeekOffset] = useState(0);
    const currentDate = moment().utc().startOf('isoWeek').add(weekOffset, 'week');
    const { breakpoint } = useWindowDimensions();
    const { data, error } = useSWR(
        spaceGroup?.id ? `space/${spaceGroup?.id}/${currentDate.year()}/${currentDate.week()}` : null
    );
    const isLoading = !data && !error;
    const noRows = data?.data.length <= 0;
    const spaceCount = data?.data.length;

    const mappedData = useMemo(
        () =>
            data?.data?.reduce(
                (carry, space, spaceIndex) =>
                    merge(
                        carry,
                        space.blockings.reduce((blockingsCarry, blocking) => {
                            const isBetweenExemptions = (dayIndex) =>
                                blocking.exemptions?.some((e) =>
                                    currentDate
                                        .clone()
                                        .set('weekday', dayIndex)
                                        .isBetween(moment(e.start), moment(e.end), 'day', '[]')
                                );
                            if (blocking.repeat_period === 'daily') {
                                const repeat_until = moment(blocking.repeat_until);
                                const isInNextWeek = repeat_until.isAfter(currentDate.clone().endOf('isoWeek'));

                                range(blocking.day_index, isInNextWeek ? 7 : repeat_until.isoWeekday()).forEach(
                                    (dayIndex) => {
                                        if (!isBetweenExemptions(dayIndex))
                                            blockingsCarry = merge(blockingsCarry, {
                                                [`${spaceIndex}-${dayIndex}`]: {
                                                    ...blockingsCarry[blocking.start_time],
                                                    [blocking.start_time]: blocking
                                                }
                                            });
                                    }
                                );
                            } else {
                                if (!isBetweenExemptions(blocking.day_index))
                                    blockingsCarry = merge(blockingsCarry, {
                                        [`${spaceIndex}-${blocking.day_index}`]: {
                                            ...blockingsCarry[blocking.start_time],
                                            [blocking.start_time]: blocking
                                        }
                                    });
                            }

                            return blockingsCarry;
                        }, {})
                    ),
                {}
            ),
        [data]
    );

    const tableBorder = useMemo(() => {
        const tableBorders = Array(7)
            .fill(true)
            .map((_, dayIndex) => {
                const mappedData = data?.data.map((space) => space.opening_hours[dayIndex]);
                return {
                    start: minBy(mappedData, (v: any) => (v.start === null ? 0 : v.start))?.start,
                    end: maxBy(mappedData, (v: any) => (v.end === null ? Infinity : v.end))?.end
                };
            });

        let start = minBy(tableBorders, (v: any) => (v.start === null ? 0 : v.start))?.start || 0;
        let end =
            maxBy(tableBorders, (v: any) => (v.end === null ? Infinity : v.end))?.end || refTable.times.length - 1;

        if (start % 4 !== 0)
            if (start - (start % 4) - 1 >= 0) start = start - (start % 4) - 1;
            else start = 0;

        if (end % 4 !== 0)
            if (end + (end % 4) < refTable.times.length) end = end - (end % 4);
            else end = refTable.times.length;

        return {
            start,
            end
        };
    }, [isLoading]);

    const rows = useMemo(
        () =>
            isLoading || noRows
                ? []
                : refTable.times
                      .map((time, timeIndex) => {
                          const { start, end } = tableBorder;
                          if (timeIndex % 4 === 0 && timeIndex >= start && timeIndex < end) {
                              const data = Object.entries(mappedData).reduce((carry, [key, value]) => {
                                  return {
                                      ...carry,
                                      [key]: Object.fromEntries(
                                          Object.entries(value).filter(([key]) =>
                                              range(timeIndex, timeIndex + 4).includes(Number(key))
                                          )
                                      )
                                  };
                              }, {});
                              return { name: time, rowKey: timeIndex, timeIndex, ...data };
                          }

                          return null;
                      })
                      .filter((item) => item !== null),
        [isLoading, noRows, mappedData]
    );

    const headerRenderer = useCallback(
        ({ cells, columns, headerIndex }: any) => {
            const headerGroupCells = [];
            let width = 0;
            let idx = 0;

            const renderHeaderTitle = (column, columnIndex) => {
                if (headerIndex === 0 && columnIndex !== 0) {
                    return currentDate
                        .clone()
                        .day(columnIndex / spaceCount)
                        .format('dd, DD.MM.YY');
                }

                if (headerIndex === 0 && columnIndex === 0) return 'Tage';
                if (headerIndex === 1 && columnIndex === 0) return 'Plätze';

                return column.title;
            };

            columns.forEach((column, columnIndex) => {
                // if there are frozen columns, there will be some placeholders for the frozen cells
                if (column[BaseTable.PlaceholderKey]) headerGroupCells.push(cells[columnIndex]);
                else {
                    width += cells[columnIndex].props.style.width;
                    idx++;

                    const nextColumn = columns[columnIndex + 1];
                    if (
                        columnIndex === columns.length - 1 ||
                        nextColumn[BaseTable.PlaceholderKey] ||
                        idx === (headerIndex === 0 ? spaceCount : 1)
                    ) {
                        headerGroupCells.push(
                            <HeaderGroupCell
                                key={`header-group-cell-${column.key}`}
                                style={{ ...cells[columnIndex].props.style, width }}
                            >
                                {renderHeaderTitle(column, columnIndex)}
                            </HeaderGroupCell>
                        );
                        width = 0;
                        idx = 0;
                    }
                }
            });
            return headerGroupCells;
        },
        [spaceCount, currentDate]
    );

    const columns: ColumnShape<{
        rowKey: string;
        isParent?: boolean;
        name: string;
        children: MappedSpace[];
    }>[] = useMemo(() => {
        return [
            {
                key: 'space-header',
                width: 65,
                flexGrow: 0,
                dataKey: 'name',
                frozen: breakpoint !== 'xs' ? 'left' : null,
                cellRenderer: ({ cellData, rowData }) => (
                    <StyledTimeHeader>
                        <span>{cellData}</span>
                        <small>bis {refTable.times?.[rowData.rowKey + 4] || '00:00'}</small>
                    </StyledTimeHeader>
                )
            },
            ...(isLoading
                ? []
                : Array(7 * spaceCount)
                      .fill(true)
                      .map((_, columnIndex) => {
                          const spaceIndex = columnIndex % spaceCount;
                          const dayIndex = Math.floor(columnIndex / spaceCount);
                          const space = data?.data?.[spaceIndex];
                          return {
                              key: String(columnIndex + 1),
                              dataKey: `${spaceIndex}-${dayIndex}`,
                              title: space.name,
                              width: columnWidth,
                              className: 'space-table-cell',
                              endOfDay: spaceIndex === spaceCount - 1,
                              dayIndex,
                              space
                          };
                      }))
        ];
    }, [spaceCount, isLoading, rows]);

    const size = !noRows ? 7 * spaceCount * columnWidth + 65 : null;

    return (
        <Flex justify="center">
            <StyledVerticalSpaceTable size={size} className={className}>
                <SpaceTableFooter>
                    <Button onClick={() => setWeekOffset(weekOffset - 1)}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Woche zurück
                    </Button>
                    <Button disabled={weekOffset === 0} variant="light" onClick={() => setWeekOffset(0)}>
                        Aktuelle Woche
                    </Button>
                    <Button onClick={() => setWeekOffset(weekOffset + 1)}>
                        Woche vor
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                </SpaceTableFooter>

                <RefetchContextProvider spaceGroup={spaceGroup} selectedDate={currentDate}>
                    <AutoResizer>
                        {({ width, height }) => (
                            <BaseTable
                                ref={tableRef}
                                width={width}
                                height={height}
                                fixed
                                columns={columns}
                                disabled={isLoading || noRows}
                                data={rows}
                                rowKey="rowKey"
                                rowClassName={'space-table-row'}
                                overscanRowCount={50}
                                headerRenderer={headerRenderer}
                                emptyRenderer={<SpaceTableLoader empty={noRows} />}
                                components={{
                                    TableCell: InjectProps(DropCell, {
                                        spaceGroup,
                                        isOwner,
                                        vertical: true,
                                        selectedDate: currentDate
                                    }) as any
                                }}
                                footerRenderer={
                                    <SpaceTableFooter>
                                        <Button onClick={() => setWeekOffset(weekOffset - 1)}>
                                            <FontAwesomeIcon icon={faArrowLeft} />
                                            Woche zurück
                                        </Button>
                                        <Button
                                            disabled={weekOffset === 0}
                                            variant="light"
                                            onClick={() => setWeekOffset(0)}
                                        >
                                            Aktuelle Woche
                                        </Button>
                                        <Button onClick={() => setWeekOffset(weekOffset + 1)}>
                                            Woche vor
                                            <FontAwesomeIcon icon={faArrowRight} />
                                        </Button>
                                    </SpaceTableFooter>
                                }
                                footerHeight={55}
                                headerHeight={[25, 45]}
                                rowHeight={100}
                            />
                        )}
                    </AutoResizer>
                </RefetchContextProvider>
            </StyledVerticalSpaceTable>
        </Flex>
    );
};

const StyledTimeHeader = styled.div`
    line-height: 1.15 !important;

    span {
        display: block;
    }

    small {
        font-size: 60%;
        color: ${({ theme }) => theme.colors.secondary};
    }
`;
